/**
 * BJ-GAP-005 — reading the server's aggregate support-access refusal.
 *
 * The bound itself lives in the database (`enforce_support_access_rate_limit`),
 * which is the only place it can be enforced. This module is purely how that
 * refusal READS: it turns the server's stable machine token into a sentence an
 * operator can act on, instead of a generic "could not create support session".
 *
 * The wire contract, set by the migration:
 *
 *   SQLSTATE  53400
 *   DETAIL    "<token> retry_at=<ISO-8601 UTC instant>"
 *
 * `retry_at` is deliberately whitespace-free so it survives being split out of
 * DETAIL. It may be absent; the message degrades to the bound alone rather
 * than inventing a time.
 *
 * Nothing here reveals another operator's activity — the server never puts it
 * in the error, and this never asks for it.
 */

/** SQLSTATE the aggregate support-access guard raises. */
export const SUPPORT_ACCESS_LIMIT_SQLSTATE = '53400';

export type SupportAccessLimitToken =
  | 'support_access_rate_limit_15m'
  | 'support_access_rate_limit_60m'
  | 'support_access_breadth_limit_60m'
  | 'support_access_breadth_limit_24h';

export interface SupportAccessLimit {
  token: SupportAccessLimitToken;
  /** The server's eligibility instant, when it supplied one. */
  retryAt: Date | null;
  /** Operator-facing sentence, retry time folded in when known. */
  description: string;
}

const BOUNDS: Record<SupportAccessLimitToken, string> = {
  support_access_rate_limit_15m:
    'You have started the maximum number of support sessions allowed in a 15-minute period.',
  support_access_rate_limit_60m:
    'You have started the maximum number of support sessions allowed in an hour.',
  support_access_breadth_limit_60m:
    'You have entered the maximum number of different shops allowed in an hour. '
    + 'Re-entering a shop you have already worked in is still available.',
  support_access_breadth_limit_24h:
    'You have entered the maximum number of different shops allowed in a 24-hour period. '
    + 'Re-entering a shop you have already worked in is still available.',
};

const isToken = (v: string): v is SupportAccessLimitToken =>
  Object.prototype.hasOwnProperty.call(BOUNDS, v);

/** "in about 6 minutes" / "at 09:12" — never a bare timestamp with no context. */
const whenAgain = (retryAt: Date, now: number): string => {
  const ms = retryAt.getTime() - now;
  if (ms <= 0) return 'You can try again now.';
  const minutes = Math.ceil(ms / 60_000);
  if (minutes < 60) {
    return `You can try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
  }
  const hours = Math.round(minutes / 60);
  return `You can try again in about ${hours} hour${hours === 1 ? '' : 's'}.`;
};

/**
 * Parse a PostgREST error into the aggregate-limit refusal it represents.
 * Returns null for anything else, so callers keep their existing handling.
 */
export function parseSupportAccessLimit(
  error: { code?: string | null; details?: string | null } | null | undefined,
  now: number = Date.now(),
): SupportAccessLimit | null {
  if (!error || error.code !== SUPPORT_ACCESS_LIMIT_SQLSTATE) return null;

  const detail = (error.details ?? '').trim();
  const token = detail.split(/\s+/)[0] ?? '';
  if (!isToken(token)) return null;

  const match = /retry_at=(\S+)/.exec(detail);
  const parsed = match ? new Date(match[1]) : null;
  const retryAt = parsed && Number.isFinite(parsed.getTime()) ? parsed : null;

  const description = retryAt
    ? `${BOUNDS[token]} ${whenAgain(retryAt, now)}`
    : BOUNDS[token];

  return { token, retryAt, description };
}

/** Convenience for call sites that only want the sentence. */
export function describeSupportAccessLimit(
  error: { code?: string | null; details?: string | null } | null | undefined,
  now: number = Date.now(),
): string | null {
  return parseSupportAccessLimit(error, now)?.description ?? null;
}
